import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  AlertTriangle,
  Droplets,
  Wind,
  CloudLightning,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function MooseJawRepair() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof repair cost in Moose Jaw?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Minor roof repairs in Moose Jaw typically range from $300 to $800, while major repairs involving structural damage or extensive leaks can cost between $1,000 and $3,000. We provide fixed-price quotes after a free inspection.",
        },
      },
      {
        "@type": "Question",
        name: "Do you handle emergency roof repairs in Moose Jaw?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we offer emergency tarping and temporary repair services to stop active leaks and protect your home from further water damage while a permanent fix is planned.",
        },
      },
      {
        "@type": "Question",
        name: "Can you fix wind-damaged shingles?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely. High prairie winds often tear off shingles. We can match your existing shingles and replace the missing sections, ensuring the surrounding area is properly sealed.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Moose Jaw",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Repair",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Moose Jaw",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Moose Jaw",
        item: "https://www.saskroofingcompany.com/areas/moose-jaw",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Repair",
        item: "https://www.saskroofingcompany.com/areas/moose-jaw/repair",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Repair Moose Jaw | Sask Roofing Company"
        description="Fast, reliable roof repair in Moose Jaw, SK. We fix leaks, wind damage, and missing shingles before they cause major structural issues. Get a free estimate."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/01402efe-8f21-42b2-bc66-0286199a2300.png&output=webp&q=60"
              alt="Moose Jaw Roof Repair"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Repair Moose Jaw
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Don't let a small leak turn into a major renovation. We provide
                fast, reliable roof repair services in Moose Jaw to fix wind
                damage, missing shingles, and active leaks. Honest assessments
                and permanent solutions.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get a Free Repair Estimate</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Stop Leaks Before They Destroy Your Home
                </h2>
                <p className="text-lg text-muted-foreground">
                  In Moose Jaw, the weather doesn't wait. A missing shingle
                  might seem like a minor cosmetic issue, but the next prairie
                  storm can drive water straight into your attic, ruining your
                  insulation and drywall.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Common roof problems we fix:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Wind className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Wind Damage:</strong> Replacing blown-off or
                        creased shingles caused by high wind speeds.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Droplets className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Active Leaks:</strong> Tracking down
                        hard-to-find leaks around chimneys, vents, and valleys.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Flashing Failure:</strong> Resealing or
                        replacing rusted and separated metal flashing.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CloudLightning className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Storm & Hail Damage:</strong> Assessing and
                        repairing localized damage after severe weather events.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3b1c76bb-88af-4db0-9bd6-efd0d1599e86.png&output=webp&q=60"
                  alt="Inspecting a leaking roof in Moose Jaw"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Why Choose Us for Moose Jaw Roof Repair?
              </h2>
              <p className="text-lg text-muted-foreground">
                We don't just patch the symptom; we find the root cause. A
                proper repair extends the life of your roof and protects your
                investment.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Honest Assessments</h3>
                <p className="text-muted-foreground">
                  If your roof can be repaired, we will repair it. We don't use
                  high-pressure sales tactics to push you into a full
                  replacement if it's not necessary.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Color Matching</h3>
                <p className="text-muted-foreground">
                  We work hard to source shingles that closely match your
                  existing roof, ensuring the repair blends in as seamlessly as
                  possible.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Permanent Fixes</h3>
                <p className="text-muted-foreground">
                  We don't just slather roofing cement over a leak. We remove
                  the damaged area, repair the underlayment, and install new
                  materials correctly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Moose Jaw
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Moose Jaw, standard residential roof
                  repairs—such as patching leaks, replacing blown-off shingles,
                  or resealing flashing—do not require a building permit.
                  However, if the repair involves fixing structural damage to
                  the roof trusses or replacing large sections of rotted
                  decking, a permit from the City of Moose Jaw may be required.
                  We assess your roof's structural integrity during our free
                  inspection and manage all necessary permitting and compliance,
                  ensuring your repair meets all local and provincial building
                  codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Moose Jaw
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Moose Jaw is no stranger to severe summer hailstorms and high
                  wind events that can cause localized roof damage. If your roof
                  has been damaged by a storm, dealing with insurance can be
                  stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  wind or hail damage, call us for a free inspection before
                  filing a claim. We will document the damage with detailed
                  photos and provide a comprehensive report for your adjuster,
                  ensuring you get the full coverage you are entitled to for a
                  proper roof repair.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Moose Jaw Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Moose Jaw roof repairs, we use premium materials designed
                  to withstand the harsh prairie climate. We source high-grade
                  sealants that remain flexible in sub-zero temperatures and use
                  architectural shingles that closely match your existing roof's
                  color and weathering. We also utilize heavy-gauge metal
                  flashing to permanently seal vulnerable areas around chimneys
                  and vents. All our repair work is backed by a robust
                  workmanship warranty, giving you peace of mind that the leak
                  is fixed for good.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      250+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Repairs in Moose Jaw
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      24h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Emergency Tarping
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Premium
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Sealants Used
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3b1c76bb-88af-4db0-9bd6-efd0d1599e86.png&output=webp&q=60"
                  alt="Moose Jaw roof repair process"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Moose Jaw Roof Repair Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  Getting your roof fixed should be straightforward and
                  stress-free.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Free Leak Diagnosis
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We inspect both the exterior of your roof and your attic
                        to pinpoint exactly where the water is entering.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Clear Estimate</h4>
                      <p className="text-muted-foreground mt-1">
                        We provide a written, fixed-price quote detailing the
                        necessary repairs. No surprise fees.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Professional Repair
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our technicians perform the repair using high-quality
                        materials designed to withstand Saskatchewan weather.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Moose Jaw roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Moose Jaw:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/moose-jaw"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Moose Jaw roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/moose-jaw/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Replacement in Moose Jaw
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Moose Jaw Roof Repair FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof repair cost in Moose Jaw?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Minor roof repairs in Moose Jaw typically range from $300 to
                  $800, while major repairs involving structural damage or
                  extensive leaks can cost between $1,000 and $3,000. We provide
                  fixed-price quotes after a free inspection.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do you handle emergency roof repairs in Moose Jaw?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Yes, we offer emergency tarping and temporary repair services
                  to stop active leaks and protect your home from further water
                  damage while a permanent fix is planned.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Can you fix wind-damaged shingles?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Absolutely. High prairie winds often tear off shingles. We can
                  match your existing shingles and replace the missing sections,
                  ensuring the surrounding area is properly sealed.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Stop the Leak Today
            </h2>
            <p className="text-xl opacity-90">
              Contact us for a free inspection and get your Moose Jaw roof
              repaired before the next storm hits.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Repair Quote</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
